// @flow
import IntlMessageFormat from 'intl-messageformat'
import memoizeIntlConstructor from 'intl-format-cache'
import debounce from 'mz-utils/debounce'
import Client from 'mz-sdk/client'
import DefaultMessagesManager from './DefaultMessagesManager'
import loadIntlPolyfill from './loadIntlPolyfill'
import loadIntlMoment from './loadIntlMoment'

// Types
import type { Message } from '../create'

export type Domains = {
  [key: string]: boolean,
}
export type Messages = {
  [key: string]: string,
}
export type Props = {
  locale: string,
  publicPath?: string,
  defaultLocale?: string,
  formats?: Object,
  messages?: Messages,
  domains?: Domains,
  children?: any,
  showMessageIds?: boolean,
  onFormatError?: (e: any) => any,
}
type LoadingResult = {
  error?: boolean,
  message: Message,
  results: Messages,
}

// Utils
function EmptyFormatter(message) {
  this.message = message
}
EmptyFormatter.prototype.format = function format() {
  return this.message
}

/**
 * Wrapper around `react-intl` which adds messages management
 * and async messages loading when message translation requested.
 * It loads translations by domains. When locale changed it wipe
 * all domains and messages and load active domains for the new locale.
 */
class BrowserMessagesManager extends DefaultMessagesManager {
  publicPath = '/'

  defaultLocale = 'en-us'

  loadingIterator = 1

  localeLoadingPromise: Promise<any> = Promise.resolve()

  domainsRequestQueue: { [key: string]: Message } = {}

  domainsRequestHandlers = {}

  formatterCache = memoizeIntlConstructor(IntlMessageFormat)

  onFormatError = null

  messages: Messages = {}

  domains: Domains = {}

  localeReady = false

  showMessageIds = false

  constructor({
    publicPath = '/',
    messages = {},
    domains = {},
    defaultLocale = 'en-us',
    locale,
    formats,
    onFormatError,
    showMessageIds,
  }: Props = {}) {
    super({ formats, locale })
    this.onFormatError = onFormatError
    this.publicPath = publicPath
    this.defaultLocale = defaultLocale
    this.messages = { ...messages }
    this.domains = { ...domains }
    this.showMessageIds = !!showMessageIds
    this.loadIntlLocale()
  }

  changeLocale(newLocale: string): boolean {
    const changeResult = super.changeLocale(newLocale)
    if (!changeResult) return false

    this.localeReady = false
    this.loadingIterator += 1
    this.domainsRequestQueue = {}
    this.domains = {}
    this.loadIntlLocale()
    return true
  }

  appendMessages(messages: Object) {
    this.messages = Object.assign(this.messages, messages)
  }

  /**
   * Returns true when given message (actually domain of the message)
   * loaded and can be used from messages object.
   * @param  {Object}  message
   * @return {Boolean}
   */
  isMessageReady(message: Message): boolean {
    return (
      this.localeReady &&
      !!(this.locale === this.defaultLocale || this.domains[message.domain])
    )
  }

  /**
   * Load domain of given message and execute given handler
   * (if provided) when the domain will loaded.
   * Internally it adds the loading request to a queue of
   * requested domains and try to get all queued domains
   * by actual queue processing function, which is debounced
   * a little bith to load domains with batches.
   * @param  {Message} message
   * @param  {Function} handler
   */
  loadMessage(message: Message, handler?: Function) {
    if (this.isMessageReady(message)) {
      handler && handler()
      return
    }

    if (handler) {
      const handlers = this.domainsRequestHandlers
      handlers[message.domain] = handlers[message.domain] || []
      handlers[message.domain].push(handler)
    }

    const queue = this.domainsRequestQueue
    if (!queue[message.domain]) {
      queue[message.domain] = message
      this.doLoadMessages()
    }
  }

  /**
   * Returns a formatter for given message string. It uses
   * internal cache for formatter instances to improve performance.
   * @param  {string} message
   * @return {Formatter}
   */
  getFormatter(message: string, customFormats?: Object) {
    // We can't use the formatter until all Intl thing polyfilled
    if (!this.localeReady || this.showMessageIds) {
      return new EmptyFormatter(message)
    }

    return super.getFormatter(message, customFormats)
  }

  /**
   * Returns translated message for given message descriptor.
   * It fallback to defaultMessage or id of the descriptor if
   * no translation for given message found.
   * @param  {Message} descr
   * @return {string}
   */
  getTranslation(descr: Message) {
    if (this.showMessageIds) {
      return descr.id
    }
    if (this.locale === this.defaultLocale) {
      return descr.defaultMessage
    }
    return this.messages[descr.id] || descr.defaultMessage || descr.id
  }

  /**
   * Translated string could be broken by the translators. Or a developer
   * could forget to give all required variables to format the message.
   * This method invoked when formatter failed to format the message and
   * it invoke provided error handler to further escalation (or logging)
   * @param  {any} e
   */
  handleFormatError(e: any) {
    console.error(e) // eslint-disable-line no-console
    if (this.onFormatError) {
      this.onFormatError(e)
    }
  }

  defaultDomainLoader = (msg: Message) => {
    if (msg.config && msg.config.disablePull) {
      return Promise.resolve({})
    }

    const msgLocale = this.localeDescriptor.alias || this.localeDescriptor.value
    const resource = (msg.config?.resource || 'web-components').toLowerCase()
    const url = `${this.publicPath}translations/${msgLocale}/${resource}-${msg.domain}.json`.toLowerCase()

    return Client.get(url, null, {
      externallyBuiltUrl: true,
      apiKey: false,
      jwt: false,
    })
  }

  doLoadMessages = debounce(50, () => {
    // It's important to have a link to a queue and loading iterator
    // to work here with exact queue object, because it can be changed
    // while the requests is in progress and then it will be a really
    // different queue object (since it wiped in `componentWillReceiveProps`)
    const currIterator = this.loadingIterator
    const queue = this.domainsRequestQueue

    const requests = Object.keys(queue).map((key) => {
      const msg = queue[key]
      const domainLoader =
        (msg.config && msg.config.domainLoader) || this.defaultDomainLoader

      return domainLoader(msg)
        .then((results) => ({ results, message: msg }))
        .catch(() => ({ results: {}, error: true, message: msg }))
    })

    this.localeLoadingPromise.then(() => {
      Promise.all(requests).then((results) => {
        if (this.loadingIterator === currIterator) {
          this.handleLoadingResults(results)
          results.forEach((res) => delete queue[res.message.domain])
        }
      })
    })
  })

  handleLoadingResults = (results: Array<LoadingResult>) => {
    results.forEach((res) => {
      const { domain } = res.message
      this.domains[domain] = true

      if (!res.error) {
        Object.assign(this.messages, res.results)
      } else {
        // in case of error for given domain we left only messages not related to it
        this.messages = Object.keys(this.messages)
          .filter((key) => !key.startsWith(domain))
          .reduce((msgs, key) => ({ ...msgs, [key]: this.messages[key] }), {})
      }

      const domainHandlers = this.domainsRequestHandlers[domain]
      this.domainsRequestHandlers[domain] = []
      if (domainHandlers) {
        domainHandlers.forEach((x) => x())
      }
    })
  }

  loadIntlLocale() {
    const requestedLocale = this.localeDescriptor.value
    const currIterator = this.loadingIterator

    this.localeLoadingPromise = Promise.all([
      loadIntlPolyfill(requestedLocale),
      loadIntlMoment(requestedLocale),
    ]).then(() => {
      if (currIterator === this.loadingIterator) {
        this.localeReady = true
      }
    })
  }

  whenLocaleReady(callback: Function) {
    this.localeLoadingPromise.then(callback, callback)
  }

  isLocaleReady() {
    return this.localeReady
  }
}

export default BrowserMessagesManager
